// src/firebase/firebase.js
import { initializeApp } from "firebase/app";
import {
    getAuth,
} from "firebase/auth";
import {
    getFirestore,
    collection,
    getDocs,
    query,
    orderBy,
    limit,
    doc,
    getDoc


} from "firebase/firestore";


import {
    getStorage,
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from "firebase/storage";

import {
    getFunctions,
} from "firebase/functions";



export const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app, 'us-central1'); // Set to us-central1 region (United States)
const functionsME = getFunctions(app, 'me-central1'); // Default region for multi-region functions



// Store ID configuration - change this for each deployment
export const DEFAULT_STORE_ID = process.env.REACT_APP_STORE_ID;

/**
 * Upload a file to Firebase Storage with cache headers
 * @param {string} storeId - The ID of the store
 * @param {string} path - The path within the store's storage
 * @param {File} file - The file to upload
 * @returns {Promise<string>} Download URL of the uploaded file
 */
const uploadFile = async (storeId, path, file) => {
    try {
        const storageRef = ref(storage, `stores/${storeId}/${path}`);

        // Set metadata with cache control headers - THIS IS THE KEY PART
        const metadata = {
            contentType: file.type,
            cacheControl: 'public, max-age=31536000', // 1 year cache
            customMetadata: {
                uploadedAt: new Date().toISOString(),
                storeId: storeId
            }
        };

        const snapshot = await uploadBytes(storageRef, file, metadata);

        const downloadURL = await getDownloadURL(snapshot.ref);

        return downloadURL;
    } catch (error) {
        throw error;
    }
};



/**
 * Upload a product image to Firebase Storage with comprehensive debugging
 * @param {string} storeId - The ID of the store
 * @param {string} productId - The ID of the product
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} Download URL of the uploaded image
 */
const uploadProductImage = async (storeId, productId, file) => {
    try {
        // Step 1: Ensure user is authenticated and get fresh token
        if (!auth.currentUser) {
            throw new Error('User not authenticated - please log in again');
        }

        // Get a fresh authentication token to avoid CORS issues
        try {
            const token = await auth.currentUser.getIdToken(true);
        } catch (tokenError) {
            throw new Error('Authentication token refresh failed - please log in again');
        }

        // Step 2: Validate inputs
        if (!storeId || !productId || !file) {
            throw new Error('Missing required parameters for upload');
        }

        // Step 3: Generate filename
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 8);
        const cleanFileName = file.name ?
            file.name.replace(/[^a-zA-Z0-9.-]/g, '_') :
            'image';
        const fileExtension = cleanFileName.split('.').pop() || 'jpg';
        const filename = `product_${timestamp}_${randomId}.${fileExtension}`;

        // Step 4: Create storage reference
        const storagePath = `stores/${storeId}/products/${productId}/${filename}`;

        const storageRef = ref(storage, storagePath);

        // Step 5: Upload with basic metadata
        const metadata = {
            contentType: file.type || 'image/jpeg',
            customMetadata: {
                uploadedBy: auth.currentUser.uid,
                uploadedAt: new Date().toISOString()
            }
        };

        const snapshot = await uploadBytes(storageRef, file, metadata);

        // Step 6: Get download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        return downloadURL;

    } catch (error) {
        // If it's a CORS error, try the simple method as fallback
        if (error.message && (error.message.includes('CORS') || error.message.includes('ERR_FAILED'))) {
            try {
                return await uploadProductImageSimple(storeId, productId, file);
            } catch (backupError) {
                throw new Error(`Upload failed due to CORS/network error. Please try again or contact support.`);
            }
        }

        throw error;
    }
};

/**
 * Alternative upload function with enhanced error handling and bucket validation
 * @param {string} storeId - The ID of the store
 * @param {string} path - The path within the store's storage
 * @param {File} file - The file to upload
 * @returns {Promise<string>} Download URL of the uploaded file
 */
const uploadFileAlternative = async (storeId, path, file) => {
    try {
        // Validate inputs
        if (!storeId || !path || !file) {
            throw new Error('Missing required parameters for upload');
        }

        // Create storage reference
        const fullPath = `stores/${storeId}/${path}`;

        const storageRef = ref(storage, fullPath);

        // Create simplified metadata
        const metadata = {
            contentType: file.type || 'application/octet-stream',
            customMetadata: {
                uploadedAt: new Date().toISOString(),
                storeId: storeId,
                originalName: file.name
            }
        };

        // Upload the file
        const snapshot = await uploadBytes(storageRef, file, metadata);

        // Get download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        return downloadURL;

    } catch (error) {
        // Provide more detailed error information
        if (error.code === 'storage/unauthorized') {
            throw new Error('Storage access denied. Please check Firebase Storage rules.');
        } else if (error.code === 'storage/invalid-url') {
            throw new Error('Invalid storage URL. Please check Firebase configuration.');
        } else if (error.code === 'storage/quota-exceeded') {
            throw new Error('Storage quota exceeded. Please contact administrator.');
        } else {
            throw new Error(`Storage error: ${error.message}`);
        }
    }
};

/**
 * Simple backup upload function with minimal configuration to avoid CORS issues
 * @param {string} storeId - The ID of the store
 * @param {string} productId - The ID of the product
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} Download URL of the uploaded image
 */
const uploadProductImageSimple = async (storeId, productId, file) => {
    try {
        // Very basic filename generation
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 8);
        const extension = 'jpg'; // Force jpg to avoid extension issues
        const filename = `img_${timestamp}_${randomStr}.${extension}`;

        // Simple storage path
        const storagePath = `stores/${storeId}/products/${productId}/${filename}`;

        // Create reference with minimal setup
        const storageRef = ref(storage, storagePath);

        // Upload with no metadata at all
        const snapshot = await uploadBytes(storageRef, file);

        // Get URL
        const url = await getDownloadURL(snapshot.ref);

        return url;

    } catch (error) {
        throw error;
    }
};


// ===== Store Statistics Functions =====


/**
 * Get dashboard stats for a store
 * OPTIMIZED: Reads from pre-calculated counters (3 reads total)
 * SIMPLIFIED: Only 3 stats - totalSales, totalOrders, averageOrderValue
 */
export const getStoreDashboardStats = async (storeId) => {
    try {
        // 1. Get pre-calculated stats (1 read)
        const statsRef = doc(db, 'stores', storeId, 'stats', 'summary');
        const statsSnap = await getDoc(statsRef);

        const statsData = statsSnap.exists() ? statsSnap.data() : {
            totalSales: 0,
            totalOrders: 0
        };

        // Calculate average order value from totals
        const averageOrderValue = statsData.totalOrders > 0
            ? statsData.totalSales / statsData.totalOrders
            : 0;

        // 2. Get recent orders for display (5 reads)
        const ordersRef = collection(db, 'stores', storeId, 'orders');
        const ordersQuery = query(
            ordersRef,
            orderBy('createdAt', 'desc'),
            limit(5)
        );
        const ordersSnap = await getDocs(ordersQuery);

        const recentOrders = ordersSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // 3. Get top products from pre-calculated data (1 read)
        const topProductsRef = doc(db, 'stores', storeId, 'stats', 'topProducts');
        const topProductsSnap = await getDoc(topProductsRef);

        const topProducts = topProductsSnap.exists()
            ? (topProductsSnap.data().products || []).slice(0, 5)
            : [];

        return {
            totalSales: statsData.totalSales || 0,
            totalOrders: statsData.totalOrders || 0,
            averageOrderValue: averageOrderValue,
            recentOrders: recentOrders,
            topProducts: topProducts
        };

    } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        throw error;
    }
};

/**
 * Delete a file from Firebase Storage
 * @param {string} fileUrl - The download URL of the file to delete
 * @returns {Promise<boolean>} True if deleted successfully
 */
const deleteFile = async (fileUrl) => {
    try {
        if (!fileUrl) return false;

        // Extract the storage path from the download URL
        // Firebase Storage URLs contain the path after /o/ and before ?
        const matches = fileUrl.match(/\/o\/(.+?)\?/);
        if (!matches || !matches[1]) {
            console.warn('Could not extract path from URL:', fileUrl);
            return false;
        }

        // Decode the URL-encoded path
        const storagePath = decodeURIComponent(matches[1]);
        const storageRef = ref(storage, storagePath);

        await deleteObject(storageRef);
        console.log('File deleted successfully:', storagePath);
        return true;
    } catch (error) {
        // If file doesn't exist, that's okay
        if (error.code === 'storage/object-not-found') {
            console.log('File not found, skipping deletion');
            return true;
        }
        console.error('Error deleting file:', error);
        return false;
    }
};

// Export Firebase instances and all functions
export {
    auth,
    db,
    storage,
    functions,
    functionsME,
    // File uploads
    uploadFile,
    uploadProductImage,
    uploadFileAlternative,
    uploadProductImageSimple,
    // File deletion
    deleteFile,
};